import { BaseSection } from "components/layout/Section/BaseSection";
import { ReferentialKey } from "api/referential";
import { useAppSelector } from "app/hooks";
import { NsKey } from "enums/NsKey";
import { InputFactory } from "factories/InputFactory";
import { AddressFormFieldNames } from "shared/enums/addressFormFieldNames";
import { selectReferentialData } from "features/referentials/selectors/selectReferentialDataByKey";
import { useFormikContext } from "formik";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { useCallback, useMemo } from "react";
import { MandantsTunnelsSelector } from "back-office/shared/components/mandants/MandantsTunnelsSelector";
import { MandantSelectorPlateforme } from "back-office/shared/components/mandants/mandantTypes";
import { pimInputsConfig } from "./pimInputsConfig";
import { PimFormFieldNames } from "./PimFormFieldNames";
import { PimFormValues } from "./PimFormValues";

import { GerantSection } from 'back-office/shared/components/gerant/GerantSection';
export const PimLayout: React.FC = () => {
  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common]);
  const { values } = useFormikContext<PimFormValues>();
  const referentialsData = useAppSelector(selectReferentialData);

  // Les plateformes sont choisies en premier : on résout leurs id + libellés
  // pour borner et grouper la recherche des mandants.
  const plateformeCodes = values[PimFormFieldNames.PlateformeIds];
  const plateformes = useMemo<MandantSelectorPlateforme[]>(() => {
    const orgRefs = referentialsData[ReferentialKey.Plateforme] ?? [];
    return (plateformeCodes ?? []).flatMap((code) => {
      const ref = orgRefs.find((r) => r.code === code);
      return ref ? [{ id: ref.id, label: ref.label }] : [];
    });
  }, [plateformeCodes, referentialsData]);

  const renderField = useCallback((fieldName: keyof PimFormValues) => {
    return (
      <InputFactory config={pimInputsConfig} fieldName={fieldName} />
    );
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <BaseSection title={t('ACTOR_FORM.PIM.SECTIONS.ORGANIZATION')}>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            {renderField(PimFormFieldNames.PlateformeIds)}
          </div>
        </div>
      </BaseSection>

      <BaseSection title={t('ACTOR_FORM.PIM.SECTIONS.MANDANTS')}>
        <MandantsTunnelsSelector
          name={PimFormFieldNames.Mandants}
          plateformes={plateformes}
        />
      </BaseSection>

      <BaseSection title={t('ACTOR_FORM.BROKER.SECTIONS.GENERAL_INFO')}>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            {renderField(PimFormFieldNames.CompanyName)}
          </div>
          <div className="col-span-1">
            {renderField(PimFormFieldNames.Phone)}
          </div>
          <div className="col-span-1">
            {renderField(PimFormFieldNames.ComplaintEmail)}
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">{t('ACTOR_FORM.PROFIL_NOTE_MANDATAIRE')}</p>
      </BaseSection>

      <BaseSection title={t('ACTOR_FORM.BROKER.SECTIONS.LEGAL_INFO')}>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-1">
            {renderField(PimFormFieldNames.RegistrationNumber)}
          </div>
          <div className="col-span-1">
            {renderField(PimFormFieldNames.Capital)}
          </div>
          <div className="col-span-1">
            {renderField(PimFormFieldNames.RcsCity)}
          </div>
          <div className="col-span-1">
            {renderField(PimFormFieldNames.RcsNumber)}
          </div>
          <div className="col-span-1">
            {renderField(PimFormFieldNames.Rcp)}
          </div>
        </div>
      </BaseSection>

      <BaseSection title={t('COMMON.LABELS.COORDINATE')}>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-6">
            {renderField(AddressFormFieldNames.Address)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.AdditionalAddress)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.PostalCode)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.City)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.Country)}
          </div>
        </div>
      </BaseSection>

      <BaseSection title={t('ACTOR_FORM.BROKER.SECTIONS.USER_INFO')}>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-1">
            {renderField(PimFormFieldNames.FirstName)}
          </div>
          <div className="col-span-1">
            {renderField(PimFormFieldNames.LastName)}
          </div>
          <div className="col-span-1">
            {renderField(PimFormFieldNames.Email)}
          </div>
          <div className="col-span-1">
            {renderField(PimFormFieldNames.Username)}
          </div>
          <div className="col-span-1">
            {renderField(PimFormFieldNames.Password)}
          </div>
        </div>
      </BaseSection>

      <GerantSection config={pimInputsConfig} />
    </div>
  );
};
